import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../hooks/useApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { TableProperties, Filter } from 'lucide-react';

// Default "Date From": today minus 15 days (local time), so the matrix opens
// on recent and upcoming editions. Older editions: change Date From by hand.
const defaultDateFrom = () => {
  const d = new Date();
  d.setDate(d.getDate() - 15);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const AdMatrixPage = () => {
  const [matrixData, setMatrixData] = useState({ editions: [], rows: [] });
  const [advertisers, setAdvertisers] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo] = useState('');
  const [advertiserFilter, setAdvertiserFilter] = useState('all');
  const [salesRepFilter, setSalesRepFilter] = useState('all');
  const [showPageNumbers, setShowPageNumbers] = useState(false);
  const [hidePSA, setHidePSA] = useState(true);
  const [colorCode, setColorCode] = useState(true);

  useEffect(() => {
    const loadRefs = async () => {
      try {
        const [advs, reps] = await Promise.all([
          api.get('/advertisers'),
          api.get('/sales-reps')
        ]);
        setAdvertisers(advs);
        setSalesReps(reps);
      } catch (err) { console.error(err); }
    };
    loadRefs();
  }, []);

  useEffect(() => {
    loadMatrix();
  }, [dateFrom, dateTo, advertiserFilter, salesRepFilter]);

  const loadMatrix = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (advertiserFilter !== 'all') params.append('advertiser_id', advertiserFilter);
      if (salesRepFilter !== 'all') params.append('sales_rep_id', salesRepFilter);
      const qs = params.toString();
      const data = await api.get(`/ad-matrix${qs ? '?' + qs : ''}`);
      setMatrixData(data);
    } catch (err) { console.error('Failed to load ad matrix:', err); }
    finally { setLoading(false); }
  };

  // Filter PSA rows if toggle enabled (a row is PSA if all its edition entries have rate 0)
  const visibleRows = useMemo(() => {
    if (!hidePSA) return matrixData.rows;
    return matrixData.rows.filter(row => {
      const vals = Object.values(row.editions || {});
      if (vals.length === 0) return true;
      return vals.some(v => (v?.rate || 0) > 0);
    });
  }, [matrixData.rows, hidePSA]);

  // Group rows by advertiser
  const groupedRows = useMemo(() => {
    const groups = {};
    for (const row of visibleRows) {
      const advName = row.advertiser_name;
      if (!groups[advName]) groups[advName] = [];
      groups[advName].push(row);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [visibleRows]);

  // Format date for column headers
  const fmtDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const editions = matrixData.editions;

  return (
    <div className="space-y-5" data-testid="ad-matrix-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-[#1E293B] flex items-center justify-center">
          <TableProperties className="w-5 h-5 text-[#3B82F6]" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-[#F8FAFC] tracking-tight">Ad Matrix</h1>
          <p className="text-[#94A3B8] mt-1">Cross-reference of bookings across editions</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[#121212] border border-[#1E293B] rounded-md p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-[#94A3B8]" />
          <span className="text-sm font-medium text-[#94A3B8]">Filters</span>
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-[#94A3B8] text-xs">Date From</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] w-40 h-9 text-sm"
              data-testid="matrix-date-from"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[#94A3B8] text-xs">Date To</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] w-40 h-9 text-sm"
              data-testid="matrix-date-to"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[#94A3B8] text-xs">Advertiser</Label>
            <Select value={advertiserFilter} onValueChange={setAdvertiserFilter}>
              <SelectTrigger className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] w-48 h-9 text-sm" data-testid="matrix-advertiser-filter">
                <SelectValue placeholder="All Advertisers" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#1E293B]">
                <SelectItem value="all" className="text-[#F8FAFC]">All Advertisers</SelectItem>
                {[...advertisers].sort((a, b) => a.business_name.localeCompare(b.business_name)).map(adv => (
                  <SelectItem key={adv.id} value={adv.id} className="text-[#F8FAFC]">{adv.business_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[#94A3B8] text-xs">Sales Rep</Label>
            <Select value={salesRepFilter} onValueChange={setSalesRepFilter}>
              <SelectTrigger className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] w-44 h-9 text-sm" data-testid="matrix-salesrep-filter">
                <SelectValue placeholder="All Reps" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#1E293B]">
                <SelectItem value="all" className="text-[#F8FAFC]">All Reps</SelectItem>
                {[...salesReps].sort((a, b) => a.name.localeCompare(b.name)).map(rep => (
                  <SelectItem key={rep.id} value={rep.id} className="text-[#F8FAFC]">{rep.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {(dateFrom || dateTo || advertiserFilter !== 'all' || salesRepFilter !== 'all') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setDateFrom(''); setDateTo(''); setAdvertiserFilter('all'); setSalesRepFilter('all'); }}
              className="text-[#94A3B8] hover:text-[#F8FAFC] h-9"
              data-testid="matrix-clear-filters"
            >
              Clear
            </Button>
          )}
          <label className="flex items-center gap-2 h-9 cursor-pointer select-none ml-auto" data-testid="matrix-show-page-numbers-toggle">
            <input
              type="checkbox"
              checked={showPageNumbers}
              onChange={(e) => setShowPageNumbers(e.target.checked)}
              className="h-4 w-4 rounded border-[#334155] bg-[#0A0A0A] accent-[#3B82F6] cursor-pointer"
              data-testid="matrix-show-page-numbers-checkbox"
            />
            <span className="text-xs text-[#94A3B8]">Show page numbers</span>
          </label>
          <label className="flex items-center gap-2 h-9 cursor-pointer select-none" data-testid="matrix-hide-psa-toggle">
            <input
              type="checkbox"
              checked={hidePSA}
              onChange={(e) => setHidePSA(e.target.checked)}
              className="h-4 w-4 rounded border-[#334155] bg-[#0A0A0A] accent-[#3B82F6] cursor-pointer"
              data-testid="matrix-hide-psa-checkbox"
            />
            <span className="text-xs text-[#94A3B8]">Hide PSA Ads</span>
          </label>
          <label className="flex items-center gap-2 h-9 cursor-pointer select-none" data-testid="matrix-color-code-toggle">
            <input
              type="checkbox"
              checked={colorCode}
              onChange={(e) => setColorCode(e.target.checked)}
              className="h-4 w-4 rounded border-[#334155] bg-[#0A0A0A] accent-[#3B82F6] cursor-pointer"
              data-testid="matrix-color-code-checkbox"
            />
            <span className="text-xs text-[#94A3B8]">Color-code</span>
          </label>
        </div>
      </div>

      {/* Matrix Table */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3B82F6]"></div>
        </div>
      ) : groupedRows.length === 0 ? (
        <div className="bg-[#121212] border border-[#1E293B] rounded-md p-12 text-center">
          <TableProperties className="w-12 h-12 mx-auto mb-3 text-[#94A3B8] opacity-40" />
          <p className="text-lg text-[#94A3B8]">No bookings found</p>
          <p className="text-sm text-[#475569] mt-1">Adjust your filters or create bookings to see the matrix</p>
        </div>
      ) : (
        <div className="bg-[#121212] border border-[#1E293B] rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm" data-testid="matrix-table">
              <thead>
                <tr className="bg-[#1E293B]">
                  <th className="sticky left-0 z-20 bg-[#1E293B] text-left py-2.5 px-3 text-xs font-semibold tracking-wide uppercase text-[#F8FAFC] min-w-[180px] border-r border-[#334155]">Advertiser</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold tracking-wide uppercase text-[#F8FAFC] min-w-[120px] border-r border-[#334155]">Booking</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold tracking-wide uppercase text-[#F8FAFC] min-w-[120px] border-r border-[#334155]">Ad Rep</th>
                  {editions.map(ed => (
                    <th key={ed.id} className="text-center py-2.5 px-2 text-[10px] font-semibold text-[#F8FAFC] min-w-[70px] border-r border-[#334155] whitespace-nowrap">
                      {fmtDate(ed.publish_date)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groupedRows.map(([advName, rows]) => (
                  <React.Fragment key={advName}>
                    {/* Booking rows */}
                    {rows.map((row, idx) => (
                      <tr key={`${advName}-${idx}`} className="border-b border-[#1E293B] hover:bg-[#1A1A1A]" data-testid={`matrix-row-${advName}-${idx}`}>
                        <td className="sticky left-0 z-10 bg-[#121212] py-1.5 px-3 text-[#F8FAFC] border-r border-[#1E293B] font-medium text-xs">{row.advertiser_name}</td>
                        <td className="py-1.5 px-3 text-[#94A3B8] border-r border-[#1E293B] text-xs">{row.product_type}</td>
                        <td className="py-1.5 px-3 text-[#94A3B8] border-r border-[#1E293B] text-xs">{row.sales_rep_name || '-'}</td>
                        {editions.map(ed => {
                          const val = row.editions[ed.id];
                          if (!val) {
                            return <td key={ed.id} className="py-1.5 px-2 text-center border-r border-[#1E293B]" />;
                          }
                          const hasPage = typeof val.page === 'number' && val.page > 0;
                          const status = val.status || 'confirmed';
                          const isPaid = status === 'paid';
                          const isInvoiced = status === 'invoiced';
                          const isPSA = (val.rate || 0) === 0;
                          const needsFacturaUnpaid = val.needs_factura && !isPaid;

                          let bg = 'bg-[#3B82F6]'; // default blue (confirmed, or simple mode)
                          let kind = 'confirmed';
                          if (colorCode) {
                            if (isPaid) { bg = 'bg-emerald-500'; kind = 'paid'; }
                            else if (needsFacturaUnpaid) { bg = 'bg-red-900'; kind = 'factura_unpaid'; }
                            else if (isInvoiced) { bg = 'bg-red-500'; kind = 'invoiced_unpaid'; }
                            else if (isPSA) { bg = 'bg-purple-500'; kind = 'psa'; }
                            else { bg = 'bg-[#3B82F6]'; kind = 'confirmed'; }
                          }
                          // Unplaced is now an OVERLAY (diagonal strikethrough), not a color —
                          // so a paid-but-unplaced ad still shows green underneath.
                          const isUnplaced = !hasPage;

                          return (
                            <td
                              key={ed.id}
                              className={`relative py-1.5 px-2 text-center border-r border-[#1E293B] ${bg}`}
                              data-testid={`matrix-cell-${advName}-${ed.id}`}
                              data-cell-kind={kind}
                              data-unplaced={isUnplaced ? 'true' : 'false'}
                              title={`${kind.replace('_', ' ').toUpperCase()}${hasPage ? ` · Page ${val.page}` : ' · Not placed'}`}
                            >
                              {hasPage && showPageNumbers ? (
                                <span className="relative z-10 text-[11px] font-bold text-white leading-none">{val.page}</span>
                              ) : null}
                              {isUnplaced && (
                                <span
                                  className="absolute bottom-0 right-0 px-0.5 text-[7px] font-bold leading-none text-black/80 pointer-events-none"
                                  data-testid={`matrix-unplaced-label-${advName}-${ed.id}`}
                                >
                                  UNPLACED
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-4 text-xs text-[#94A3B8]">
          <span>{groupedRows.length} advertiser{groupedRows.length !== 1 ? 's' : ''}</span>
          <span>{matrixData.rows.length} booking{matrixData.rows.length !== 1 ? 's' : ''}</span>
          <span>{editions.length} edition{editions.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="flex items-center gap-4 text-xs text-[#94A3B8] flex-wrap" data-testid="matrix-legend">
          {colorCode ? (
            <>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-[#3B82F6]" />Confirmed</div>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-red-500" />Invoiced Unpaid</div>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-red-900" />Factura Unpaid</div>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-emerald-500" />Paid</div>
              {!hidePSA && <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-purple-500" />PSA</div>}
              <div className="flex items-center gap-1.5">
                <span className="relative inline-flex w-6 h-3 rounded-sm bg-[#3B82F6]" aria-hidden="true">
                  <span className="absolute bottom-0 right-0 px-0.5 text-[6px] font-bold leading-none text-black/80">UNPLACED</span>
                </span>
                Unplaced
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-[#3B82F6]" />Booked</div>
              <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-black border border-[#1E293B]" />Empty</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdMatrixPage;
